import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

/** A phone's long edge never exceeds this; used to tell a phone in landscape from a small desktop window. */
const PHONE_LONG_EDGE_MAX = 1024;
const NARROW_EDGE_MAX = 810;
const PHONE_SHORT_EDGE_MAX = 600;
/** Same width threshold as `respond-to(md)` in `src/_mixins.scss`. */
const WIDE_MIN_WIDTH = 1024;

interface ViewportSize {
  width: number;
  height: number;
}

/**
 * Whether the wizard should render its mobile layout. Mirrors the `layoutWide1024` rule used elsewhere
 * in the app (wide = wider than 1024px and not a phone turned sideways), so TS and `respond-to(md)` agree.
 */
@Injectable({ providedIn: 'root' })
export class NewRegistrationViewport {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly size = signal<ViewportSize>(this.measure());

  readonly isMobile = computed(() => !isWide(this.size()));

  constructor() {
    if (!this.isBrowser) {
      return;
    }
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.size.set(this.measure()));
  }

  private measure(): ViewportSize {
    if (!this.isBrowser) {
      return { width: WIDE_MIN_WIDTH + 1, height: WIDE_MIN_WIDTH + 1 };
    }
    return { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight };
  }
}

function isWide({ width, height }: ViewportSize): boolean {
  const narrowEdge = Math.min(width, height, NARROW_EDGE_MAX);
  const phoneLike = narrowEdge < PHONE_SHORT_EDGE_MAX && Math.max(width, height) <= PHONE_LONG_EDGE_MAX;
  return width > WIDE_MIN_WIDTH && !phoneLike;
}
